import {
  Body2,
  Box,
  Button,
  Container,
  Display2,
  Flex,
  Grid,
  GridItem,
} from '@sainsburys-tech/fable'
import dayjs from 'dayjs'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { isFeatureEnabled } from '../../../common/api/featureApi'
import { useApiClient } from '../../../common/AppContext/appContext'
import ErrorRefresh from '../../../common/components/ErrorRefresh'
import Header from '../../../common/components/Header'
import Loading from '../../../common/components/Loading'
import ProgressLoading from '../../../common/components/ProgressLoading'
import { Context } from '../../../common/Context/context'
import { ErrorMessage } from '../../../common/enums/ErrorMessage'
import { Feature } from '../../../common/enums/FeatureEnum'
import { headerPlannerTitles } from '../../../common/enums/HeaderItems'
import { Menu } from '../../../common/enums/MenuEnum'
import { PlannerName } from '../../../common/enums/PlannerNameEnum'
import useComponentInitialization from '../../../common/hooks/useComponentInit'
import useErrorHandler from '../../../common/hooks/useErrorHandler'
import { IResult } from '../../../common/types/IResult'
import { checkStore, getUserStore } from '../../../utils/localStore'
import {
  createCabinetStatus,
  createCounterTemperatures,
  getCounters,
} from '../../api/counterApi'
import { AlertMessageKey } from '../../enums/AlertMessageKey'
import { BatchTray, Shelf } from '../../enums/TemperatureCheckPosition'
import ICounterCheck from '../../types/ICounterCheck'
import { ICounterTemperature } from '../../types/ICounterTemperature'
import { ICreateCounterTemperaturesResponse } from '../../types/ICreateCounterTemperaturesResponse'
import { ITemperatureData } from '../../types/ITemperatureData'
import {
  afternoonHour,
  getCounterByNumber,
  hasAnyCounterToFirstCheckFrom,
  isAllTemperatureChecked,
  updateCounterCheckStatusByTime,
  validCounterTemperatures,
} from '../../utils/hotFoodUtils'
import CabinetStatusModal from './CabinetStatusModal'
import CounterAlertModal from './CounterAlertModal'
import CounterCard from './CounterCard'
import CounterInstruction from './CounterInstruction'
import CounterManualInputModal from './CounterManualInputModal'
import PostSubmitMessageCard from './PostSubmitMessageCard'

interface ICounterAlertConfig {
  alertKey?: AlertMessageKey
  counterNumber?: number
  opened: boolean
}

const CounterTemperatureView = () => {
  const { dispatch } = useContext(Context)
  const apiClient = useApiClient()

  const store = useMemo(() => getUserStore(), [])

  const [isDataLoading, setIsDataLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [shelves, setShelves] = useState<ICounterCheck[]>([])
  const [isFirstCheck, SetIsFirstCheck] = useState<boolean>(true)
  const [submitBufferData, SetSubmitBufferData] = useState<ITemperatureData[]>(
    [],
  )
  const [hasAction, setHasAction] = useState(false)
  const [isInstantSubmissionMessage, setIsInstantSubmissionMessage] =
    useState(false)
  const [alertConfig, setAlertConfig] = useState<ICounterAlertConfig>({
    alertKey: AlertMessageKey.NONE,
    counterNumber: 1,
    opened: false,
  })
  const [faultyOrFixedCabinet, setFaultyOrFixedCabinet] = useState<
    ICounterCheck | undefined
  >(undefined)
  const [isManualInputModalOpen, setIsManualInputModalOpen] = useState(false)
  const [isBluetoothReading, setIsBluetoothReading] = useState(true)

  const varShelvesRef = useRef<ICounterCheck[]>([])

  const { handleError, resetError } = useErrorHandler(setErrorMessage)

  const now = useCallback(() => dayjs(), [])

  // it's called by 1) initial loading and 2) reRendering
  const updateCounterCardStatus = useCallback((counters: ICounterCheck[]) => {
    // update main state variable of counter object
    setShelves(counters)

    // update the global variable to refer it on every refresh
    varShelvesRef.current = counters

    // it will check if there is a remain action to take among cabinets
    setHasAction(
      !isAllTemperatureChecked(counters) ||
        counters.filter((tc) => tc.isFaulty).length > 0,
    )
  }, [])

  const loadDataProcess = useCallback(
    async (refreshShelvesStatus: boolean) => {
      try {
        const countersData = await getCounters(
          apiClient,
          store.storeId,
          now().toDate(),
        )

        const storedShelves: ICounterCheck[] = countersData.map((counter) => {
          return {
            counterNumber: counter.counterNumber,
            isFaulty: counter.isFaulty,
            shelves: counter.shelves,
            takenAt: counter.takenAt !== null ? counter.takenAt : undefined,
            temperatures: undefined,
          }
        })

        if (storedShelves.length > 0) {
          const updatedShelves = storedShelves.reduce(
            (accShelves: ICounterCheck[], current: ICounterCheck) => [
              ...accShelves,
              updateCounterCheckStatusByTime(current, current.takenAt),
            ],
            [],
          )

          if (refreshShelvesStatus) {
            updateCounterCardStatus(updatedShelves)
          }
        }
      } catch (e) {
        handleError(
          ErrorMessage.CounterTemperatureView.FailedToLoad,
          e as Error,
        )
      } finally {
        setIsDataLoading(false)
      }
    },
    [apiClient, store.storeId, handleError, now, updateCounterCardStatus],
  )

  const refresh = useCallback(() => {
    let refreshCounter = true

    const allTempsChecked = isAllTemperatureChecked(
      varShelvesRef.current.filter((tc) => !tc.isFaulty),
    )

    // all counter temperatures taken for non-faulty cabinets?
    if (allTempsChecked) {
      varShelvesRef.current
        .filter((c) => !c.isFaulty)
        .forEach((c) => {
          const takenTime = dayjs(c.takenAt)
          if (
            now().get('hour') < afternoonHour ||
            takenTime.get('hour') >= afternoonHour
          ) {
            refreshCounter = false
          }
        })
    }

    if (allTempsChecked && refreshCounter) {
      loadDataProcess(allTempsChecked && refreshCounter)
    }
  }, [loadDataProcess, now])

  const { headerNavigationItems, isLoading: initLoading } =
    useComponentInitialization(
      apiClient,
      store,
      dispatch,
      PlannerName.HotFood,
      handleError,
    )

  const isLoading = initLoading || isDataLoading

  useEffect(() => {
    const intervalId = setInterval(refresh, 10000)

    return () => {
      clearInterval(intervalId)
    }
  }, [refresh])

  useEffect(() => {
    const loadInitialProcess = async () => {
      setIsDataLoading(true)
      resetError() // Clear previous errors

      try {
        const isStoreZeroErrorsEnabled = await isFeatureEnabled(
          apiClient,
          Feature.StoreZeroErrors,
          0,
        )

        if (isStoreZeroErrorsEnabled) {
          const storeError = checkStore(store)
          if (storeError) {
            handleError(storeError)
          }
        }
      } catch (e) {
        handleError(ErrorMessage.ProductionView.FailedToLoad, e as Error)
      } finally {
        setIsDataLoading(false)
      }
    }

    loadInitialProcess()
    loadDataProcess(true)
  }, [apiClient, store, handleError, resetError, loadDataProcess])

  useEffect(() => {
    const script = document.createElement('script')
    let probeEnabled = false

    setIsBluetoothReading(true)

    script.src = './probe.js'
    document.head.append(script)

    if (
      (window as Window).webkit &&
      (window as Window).webkit!.messageHandlers.bluetoothMessage
    ) {
      ;(window as Window).webkit!.messageHandlers.bluetoothMessage.postMessage(
        'start',
      )

      probeEnabled = true
    }

    return () => {
      if (probeEnabled) {
        document.head.removeChild(script)
        if (
          (window as Window).webkit &&
          (window as Window).webkit!.messageHandlers.bluetoothMessage
        ) {
          ;(
            window as Window
          ).webkit!.messageHandlers.bluetoothMessage.postMessage('stop')
        }
      }
    }
  }, [])

  // 1) after new temp is added
  // 2) after new temp is deleted
  const reRenderUpdatedCounters = (counters: ICounterCheck[]) => {
    updateCounterCardStatus(counters)

    SetIsFirstCheck(hasAnyCounterToFirstCheckFrom())

    // not missing the usecases, reset flag here.
    setIsInstantSubmissionMessage(false)
  }

  const onCloseFaultyOrFixedStillNotFixed = () => {
    // close Status dialog
    setFaultyOrFixedCabinet(undefined)

    // open "still not fixed" dialog
    setAlertConfig({
      alertKey: AlertMessageKey.ShelfUnder75DegTemperatureNotFixed,
      opened: true,
    })
  }

  const onSaveFaultyOrFixed = async (data: ICounterCheck) => {
    setFaultyOrFixedCabinet(undefined)

    if (data.isFaulty) {
      // user has marked as faulty
      createCabinetStatus(apiClient, {
        counterNumber: data.counterNumber,
        isFaulty: data.isFaulty,
        storeId: store.storeId,
      })
        .then((res: IResult) => {
          if (res.isSuccess) {
            // mark cabinet as Faulty
            reRenderUpdatedCounters(
              updateCountersByFaulty(
                shelves,
                data.counterNumber,
                data.isFaulty,
              ),
            )
          } else {
            handleError(res.message)
          }
        })
        .catch((e) => {
          handleError(ErrorMessage.SaveException, e as Error)
        })
    } else {
      // user has marked as fixed
      const createCabinetStatusTask = createCabinetStatus(apiClient, {
        counterNumber: data.counterNumber,
        isFaulty: data.isFaulty,
        storeId: store.storeId,
      })

      const createCounterTemperaturesTask = createCounterTemperatures(
        apiClient,
        {
          cabinetId: data.counterNumber,
          isBluetoothReading,
          storeId: store.storeId,
          temperatures: data.temperatures!.reduce(
            (
              accTs: Array<{
                position: BatchTray | Shelf
                temperature: number
              }>,
              t,
            ) => {
              if (t.temperature) {
                return [
                  ...accTs,
                  {
                    position: t.position,
                    temperature: Number(t.temperature),
                  },
                ]
              }
              return accTs
            },
            [],
          ),
        },
      )

      Promise.all([createCabinetStatusTask, createCounterTemperaturesTask])
        .then((responses: [IResult, ICreateCounterTemperaturesResponse]) => {
          if (responses[0].isSuccess) {
            // mark cabinet as Fixed
            const counters = shelves
            const updatedCounters = counters.reduce(
              (accCounter: ICounterCheck[], current: ICounterCheck) => {
                if (current.counterNumber === data.counterNumber) {
                  current.isFaulty = data.isFaulty
                }
                return [...accCounter, current]
              },
              [],
            )

            // update temperature taken at time
            const newCounter = responses[1].data as ICounterTemperature

            const ctr = updateCountersByTakenTime(
              updatedCounters,
              newCounter.counterNumber,
              newCounter.takenAt,
            )

            reRenderUpdatedCounters(ctr)
          } else {
            handleError(responses[0].message)
          }
        })
        .catch((e) => {
          handleError(ErrorMessage.SaveException, e as Error)
        })
    }
  }

  const handleFaultyOrFixed = (data: ICounterCheck) => {
    setFaultyOrFixedCabinet(data)
  }

  const handleSubmit = async (
    counterNumber: number,
    data: ITemperatureData[],
    isBtReading: boolean,
  ) => {
    // if more than an input box is empty, invalid error message and just close
    const emptyTemps = data.filter((t) => t.temperature === '')
    if (emptyTemps.length > 0) {
      setAlertConfig({
        alertKey: AlertMessageKey.InValidInputData,
        counterNumber,
        opened: true,
      })
      return
    }

    // temporary temperatures data for a modal box event data handling
    SetSubmitBufferData(data)

    const counter = getCounterByNumber(shelves, counterNumber)
    if (!counter) {
      setAlertConfig({
        alertKey: AlertMessageKey.SomethingWrong,
        counterNumber,
        opened: true,
      })
      return
    }

    const [isValid, resultMessageType] = validCounterTemperatures(data, counter)

    let showAlert = !isValid

    switch (resultMessageType) {
      case AlertMessageKey.TopShelfOverMaxTemperature:
      case AlertMessageKey.MiddleFirstShelfOverMaxTemperature:
      case AlertMessageKey.MiddleSecondShelfOverMaxTemperature:
      case AlertMessageKey.BottomShelfOverMaxTemperature:
        showAlert = true
    }

    if (showAlert) {
      setAlertConfig({
        alertKey: resultMessageType as AlertMessageKey,
        counterNumber,
        opened: true,
      })
      return
    }

    setAlertConfig({ opened: false })

    // normal case -> proceed to save with validated temp data
    await handleSaveCounterTemperature(counterNumber, data, false, isBtReading)

    // only handle the instant submission message after successfully saved.
    // this value MUST set after the above function 'handleSaveCounterTemperature'
    // because reRenderUpdateCounter will reset this flag to false.
    setIsInstantSubmissionMessage(true)
    return
  }

  const handleSaveCounterTemperature = async (
    cabinetId: number,
    data: ITemperatureData[],
    isFaultyByApp: boolean,
    isBtReading: boolean,
  ) => {
    setIsDataLoading(true)

    await createCounterTemperatures(apiClient, {
      cabinetId,
      isBluetoothReading: isBtReading,
      storeId: store.storeId,
      temperatures: data.reduce(
        (
          accTs: Array<{
            position: BatchTray | Shelf
            temperature: number
          }>,
          t,
        ) => {
          if (t.temperature) {
            return [
              ...accTs,
              {
                position: t.position,
                temperature: Number(t.temperature),
              },
            ]
          }
          return accTs
        },
        [],
      ),
    })
      .then((response: ICreateCounterTemperaturesResponse) => {
        setIsDataLoading(false)

        // if success, display a message card with time under the input card
        if (response.isSuccess) {
          const newCounter = response.data as ICounterTemperature
          const counters = updateCountersByTakenTime(
            shelves,
            cabinetId,
            newCounter.takenAt,
          )

          // if the temp data is stored with insufficient values, it should be updated with a faulty on counter status
          const updatedCounters = isFaultyByApp
            ? updateCountersByFaulty(counters, cabinetId, isFaultyByApp)
            : counters

          reRenderUpdatedCounters(updatedCounters)
        }

        if (response.isSuccess) {
          resetError()
        } else {
          handleError(response.message)
        }
      })
      .catch((e) => {
        handleError(ErrorMessage.SaveException, e as Error)
      })
      .finally(() => {
        setIsDataLoading(false)
      })
  }

  const updateCountersByFaulty = (
    targetCounters: ICounterCheck[],
    counterNumber: number,
    isFaulty: boolean,
  ) =>
    targetCounters.reduce(
      (accCounter: ICounterCheck[], current: ICounterCheck) => {
        if (current.counterNumber === counterNumber) {
          current.isFaulty = isFaulty
        }
        return [...accCounter, current]
      },
      [],
    )

  const updateCountersByTakenTime = (
    counters: ICounterCheck[],
    counterNumber: number,
    takenTime: Date,
  ) => {
    const updatedCounters = counters.reduce(
      (accCounter: ICounterCheck[], current: ICounterCheck) => {
        if (current.counterNumber === counterNumber) {
          return [
            ...accCounter,
            updateCounterCheckStatusByTime(current, takenTime),
          ]
        }
        return [...accCounter, current]
      },
      [],
    )

    return updatedCounters
  }

  // according to an action of modal box, next event will be handled
  const handleAction = async () => {
    switch (alertConfig.alertKey) {
      case AlertMessageKey.ShelfUnder75DegTemperature:
      case AlertMessageKey.ShelfBetween63And55DegTemperature:
      case AlertMessageKey.ShelfBelow55DegTemperature:
        await handleSaveCounterTemperature(
          alertConfig.counterNumber ? alertConfig.counterNumber : 0,
          submitBufferData,
          true,
          isBluetoothReading,
        )
        break

      case AlertMessageKey.TopShelfOverMaxTemperature:
      case AlertMessageKey.MiddleFirstShelfOverMaxTemperature:
      case AlertMessageKey.MiddleSecondShelfOverMaxTemperature:
      case AlertMessageKey.BottomShelfOverMaxTemperature:
        // normal case -> proceed to save with validated (albeit warmer) temp data
        await handleSaveCounterTemperature(
          alertConfig.counterNumber ? alertConfig.counterNumber : 0,
          submitBufferData,
          false,
          isBluetoothReading,
        )

        // only handle the instant submission message after successfully saved.
        // this value MUST set after the above function 'handleSaveCounterTemperature'
        // because reRenderUpdateCounter will reset this flag to false.
        setIsInstantSubmissionMessage(true)
        break

      default:
        break
    }

    setAlertConfig({ opened: false })
  }

  const renderCounterCardItems = (counters: ICounterCheck[]) =>
    counters.map((counter, i) => (
      <GridItem key={i} colSpan={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
        <CounterCard
          counterNumber={counter.counterNumber}
          isFaulty={counter.isFaulty}
          tempCheckStatus={counter.checkedStatus}
          title={`Turbo-serve ${counter.counterNumber}`}
          handleSubmit={handleSubmit}
          handleFaultyOrFixed={handleFaultyOrFixed}
          isFirstCheck={isFirstCheck}
          isBluetoothReading={isBluetoothReading}
          shelves={counter.shelves}
        />
      </GridItem>
    ))

  const renderPage = () => (
    <Box backgroundColor='primary' padding='none'>
      <Header
        title={headerPlannerTitles.hotfood}
        navItems={headerNavigationItems}
        activeMenuType={Menu.CounterTemperature}
      />

      {/* Alert Modal box to do further actions: delete, save, close by the alertKeys */}
      {alertConfig.opened && (
        <CounterAlertModal
          isOpen={alertConfig.opened}
          alertKey={alertConfig.alertKey}
          handleClose={() => setAlertConfig({ opened: false })}
          handleAction={handleAction}
        />
      )}
      <Container
        style={{
          backgroundColor:
            'var(--fable-color-background-neutral-subtlest-default)',
          padding: '32px 0',
          maxWidth: 1400,
          margin: '0 auto',
        }}
      >
        {isLoading && <Loading />}
        {errorMessage && <ErrorRefresh message={errorMessage} />}

        {!isLoading && !errorMessage && (
          <>
            <Grid cols={12}>
              <GridItem colSpan={6}>
                <Flex direction='column' gap='sm'>
                  <Box padding={{ sm: 'sm' }}>
                    <Display2 style={{ marginTop: 0, marginBottom: '8px' }}>
                      Counter temperature checks
                    </Display2>
                    <Body2>Add temperature checks from turbo-serves.</Body2>
                  </Box>
                </Flex>
              </GridItem>
              <GridItem colSpan={6}>
                <Flex direction='column' alignItems='flex-end' gap='sm'>
                  <Box
                    as='div'
                    className='c-availability-button'
                    padding={{ sm: 'sm' }}
                  >
                    {isBluetoothReading ? (
                      <Button
                        testid='manual-input-modal-button'
                        onClick={() => setIsManualInputModalOpen(true)}
                        variant='secondary'
                      >
                        Change to manual checks
                      </Button>
                    ) : (
                      <Button
                        testid='bluetooth-input-button'
                        onClick={() => setIsBluetoothReading(true)}
                      >
                        Change back to Bluetooth checks
                      </Button>
                    )}
                    <Box
                      as='div'
                      style={{ fontSize: 'var(--fable-text-body-1-font-size)' }}
                    >
                      <span id='probe-status' data-testid='probe-status'></span>
                    </Box>
                  </Box>
                </Flex>
              </GridItem>
            </Grid>
            <Grid cols={12} gap='sm'>
              <GridItem colSpan={12}>
                {!isAllTemperatureChecked(shelves) && (
                  <CounterInstruction
                    isFirstCheck={isFirstCheck}
                    isInputMode={hasAction}
                  />
                )}
              </GridItem>
              {hasAction ? (
                renderCounterCardItems(shelves)
              ) : (
                <GridItem colSpan={12}>
                  <PostSubmitMessageCard
                    counters={shelves}
                    isInstantMessage={isInstantSubmissionMessage}
                  />
                </GridItem>
              )}
            </Grid>
          </>
        )}

        {faultyOrFixedCabinet && (
          <CabinetStatusModal
            cabinet={faultyOrFixedCabinet}
            handleClose={() => setFaultyOrFixedCabinet(undefined)}
            handleCloseStillNotFixed={() => onCloseFaultyOrFixedStillNotFixed()}
            handleSave={onSaveFaultyOrFixed}
            isOpen={faultyOrFixedCabinet !== undefined}
            isBluetoothReading={isBluetoothReading}
          />
        )}

        {isManualInputModalOpen && (
          <CounterManualInputModal
            handleClose={() => setIsManualInputModalOpen(false)}
            handleSave={(value) => {
              setIsBluetoothReading(value)
              setIsManualInputModalOpen(value)
            }}
            isOpen={isManualInputModalOpen}
          />
        )}
      </Container>
    </Box>
  )

  return (
    <>
      {isLoading && <ProgressLoading message='Loading...' />}

      {!isLoading && <>{renderPage()}</>}
    </>
  )
}

export default CounterTemperatureView
